import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../../api';
import { useResource } from '../../hooks';
import { Field, Input, Section } from '../primitives';
import { ConfirmDialog, ErrorNotice } from '../ui';
import { QuietEmpty } from '../project/ui';
import { relativeTime } from '../project/projectHealth';

/**
 * CloudAccountsPanel (CO-200): the Hetzner API tokens an organization saves
 * so Add server can create servers in its own account. An owner adds and
 * removes them; the token is checked with Hetzner, stored sealed and never
 * shown again.
 */
export function CloudAccountsPanel({ isOwner }: { isOwner: boolean }) {
  const [reload, setReload] = useState(0);
  const tokens = useResource(() => (isOwner ? api.listCloudTokens() : Promise.resolve(null)), [isOwner, reload], undefined, { key: 'cloud-tokens' });
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  if (!isOwner) {
    return (
      <Section title="Cloud accounts" testId="cloud-accounts">
        <QuietEmpty>Only an owner can manage cloud accounts</QuietEmpty>
      </Section>
    );
  }

  async function add(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.addCloudToken({ provider: 'hetzner', name: name.trim(), token: token.trim() });
      setName('');
      setToken('');
      setReload((n) => n + 1);
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.deleteCloudToken(id);
      setRemoving(null);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  }

  const list = tokens.data?.tokens ?? [];
  const removingToken = list.find((t) => t.id === removing);
  return (
    <Section title="Cloud accounts" testId="cloud-accounts">
      {tokens.error && !tokens.data ? <ErrorNotice error={tokens.error} /> : null}
      {list.length > 0 ? (
        <ul className="settings-list" aria-label="Saved cloud tokens">
          {list.map((t) => (
            <li key={t.id} className="settings-list__row">
              <span>
                <strong>{t.name}</strong> <span className="muted">Hetzner · added {relativeTime(t.created_at)}</span>
              </span>
              <button type="button" className="button button--danger-quiet button--small" disabled={busy} onClick={() => setRemoving(t.id)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : tokens.data ? (
        <QuietEmpty>No Hetzner token saved</QuietEmpty>
      ) : null}
      <ErrorNotice error={error} />
      <form className="stack" onSubmit={add}>
        <Field label="Name" hint="Shown when you create a server">
          <Input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Production project" autoComplete="off" required maxLength={80} data-testid="cloud-token-name" />
        </Field>
        <Field label="Hetzner API token" hint="Read & Write, from the project's Security page">
          <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" spellCheck={false} required data-testid="cloud-token-value" />
        </Field>
        <div className="row-actions">
          <button type="submit" className="button button--primary" disabled={busy || name.trim() === '' || token.trim() === ''} data-testid="cloud-token-save">
            {busy ? 'Checking' : 'Save token'}
          </button>
        </div>
      </form>
      {removingToken ? (
        <ConfirmDialog
          open={true}
          title={`Remove ${removingToken.name}?`}
          description="Servers created with it keep running."
          confirmLabel="Remove token"
          danger={true}
          busy={busy}
          onConfirm={() => remove(removingToken.id)}
          onCancel={() => setRemoving(null)}
        />
      ) : null}
    </Section>
  );
}
